import { useEffect, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import Seo from '@/components/Seo'
import { courses } from '@/data/catalog'
import { useAuth } from '@/hooks/useAuth'
import { downloadCertificate } from '@/lib/certificate'
import { fetchMyEnrollments, statusLabel, type CourseEnrollment } from '@/lib/enrollments'
import { getEnrollment, progressPercent } from '@/lib/progress'

function Dashboard() {
  const { isAuthenticated, isAdmin, user } = useAuth()
  const [requests, setRequests] = useState<CourseEnrollment[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const userId = user?.userId
    if (!userId) {
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    fetchMyEnrollments(userId)
      .then((list) => {
        if (!cancelled) setRequests(list)
      })
      .catch((err) => {
        console.error('No se pudieron cargar las inscripciones:', err)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [user?.userId])

  if (!isAuthenticated) {
    return <Navigate to="/acceso?next=/panel" replace />
  }

  return (
    <>
      <Seo title="Panel" description="Sigue tu progreso en HFCursos y descarga certificados al completar cada curso." />
      <section className="mx-auto max-w-6xl px-5 py-12 lg:px-8">
        <p className="text-xs uppercase tracking-[0.28em] text-[#C41E3A]">Estudio personal</p>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <h1 className="font-['Fraunces'] text-4xl">Hola, {user?.userName || 'estudiante'}</h1>
          {isAdmin ? (
            <Link to="/admin" className="border border-[#1E3A5F] px-3 py-2 text-sm">
              Revisar solicitudes
            </Link>
          ) : null}
        </div>
        <p className="mt-3 max-w-xl text-sm text-[#1E3A5F]/70">
          Elige un curso inscrito para abrir sus módulos, assignments y el aula cuando esté autorizada.
        </p>

        {loading ? (
          <p className="mt-12 text-sm text-[#1E3A5F]/60">Cargando tus cursos…</p>
        ) : requests.length === 0 ? (
          <div className="mt-12 border border-dashed border-[#1E3A5F]/20 p-8">
            <p className="font-['Fraunces'] text-2xl">Todavía no hay cursos</p>
            <p className="mt-2 text-sm text-[#1E3A5F]/65">Elige un programa y solicita la inscripción desde su ficha.</p>
            <Link to="/cursos" className="mt-5 inline-block bg-[#1E3A5F] px-4 py-2 text-sm text-[#F4EFE6]">
              Ir al catálogo
            </Link>
          </div>
        ) : (
          <ul className="mt-10 grid gap-6 md:grid-cols-2">
            {requests.map((request) => {
              const course = courses.find((item) => item.id === request.courseId)
              if (!course) return null
              const local = user?.userId ? getEnrollment(user.userId, course.id) : undefined
              const percent = progressPercent(local, course.modules.length)
              const open = request.status === 'approved' && request.documentsAccess
              return (
                <li key={request._id} className="border border-[#1E3A5F]/10 p-5">
                  <p className="text-xs uppercase tracking-wide text-[#C41E3A]">{statusLabel(request.status)}</p>
                  <h2 className="mt-1 font-['Fraunces'] text-2xl">
                    <Link to={`/panel/${course.slug}`} className="hover:underline">
                      {course.title}
                    </Link>
                  </h2>
                  <p className="mt-2 text-sm text-[#1E3A5F]/60">
                    {request.documentsAccess ? 'Documentos desbloqueados' : 'Documentos pendientes de autorización'}
                  </p>
                  {open ? (
                    <>
                      <p className="mt-3 text-sm text-[#1E3A5F]/60">Progreso {percent}%</p>
                      <div className="mt-2 h-1 bg-[#1E3A5F]/10">
                        <div className="h-full bg-[#C41E3A]" style={{ width: `${percent}%` }} />
                      </div>
                    </>
                  ) : null}
                  <div className="mt-5 flex flex-wrap gap-3">
                    <Link to={`/panel/${course.slug}`} className="bg-[#1E3A5F] px-3 py-2 text-sm text-[#F4EFE6]">
                      Abrir curso
                    </Link>
                    {open ? (
                      <Link to={`/aula/${course.slug}`} className="border border-[#1E3A5F] px-3 py-2 text-sm">
                        Ir al aula
                      </Link>
                    ) : (
                      <Link to={`/cursos/${course.slug}`} className="border border-[#1E3A5F] px-3 py-2 text-sm">
                        Ver ficha
                      </Link>
                    )}
                    {open && local?.completedAt ? (
                      <button
                        type="button"
                        onClick={() =>
                          downloadCertificate({
                            studentName: request.fullName || user?.userName || 'Estudiante',
                            courseTitle: course.title,
                            completedAt: local.completedAt || new Date().toISOString(),
                          })
                        }
                        className="border border-[#1E3A5F] px-3 py-2 text-sm"
                      >
                        Descargar certificado
                      </button>
                    ) : null}
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </section>
    </>
  )
}

export default Dashboard
